import React, { useState, useRef, useEffect } from 'react';
import { FaSpinner, FaEnvelope, FaRedo } from 'react-icons/fa';
import '../styles/Auth.css';

export default function OTPVerification({ sessionId, email, onVerify, onResend, error, loading }) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef([]);

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    if (otp.every(digit => digit !== '') && !loading) {
      handleSubmit();
    }
  }, [otp]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Only process if it's 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      // Focus last input
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async () => {
    const otpCode = otp.join('');
    if (otpCode.length === 6) {
      await onVerify(otpCode);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) {
      return;
    }

    setIsResending(true);
    try {
      await onResend();
      setResendCooldown(60); // 60 second cooldown
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      console.error('Failed to resend OTP:', err);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="otp-verification-container">
      <div className="otp-header">
        <div className="otp-icon">
          <FaEnvelope />
        </div>
        <h2>Enter Verification Code</h2>
        <p>We've sent a 6-digit code to</p>
        <p className="otp-email">{email}</p>
      </div>

      <div className="otp-input-container">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength="1"
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            className="otp-input"
            disabled={loading}
            autoFocus={index === 0}
          />
        ))}
      </div>

      {error && (
        <div className="otp-error">
          {error}
        </div>
      )}

      <div className="otp-actions">
        <button
          type="button"
          onClick={handleResend}
          disabled={resendCooldown > 0 || isResending || loading}
          className="otp-resend-button"
        >
          {isResending ? (
            <>
              <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
              Sending...
            </>
          ) : resendCooldown > 0 ? (
            `Resend code in ${resendCooldown}s`
          ) : (
            <>
              <FaRedo style={{ marginRight: '8px' }} />
              Resend Code
            </>
          )}
        </button>
      </div>

      <div className="otp-info">
        <p>Didn't receive the code? Check your spam folder or try resending.</p>
        <p className="otp-expiry">Code expires in 10 minutes</p>
      </div>
    </div>
  );
}
